import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
// Components
import { Button, Message } from '@zolteam/zol-admin-ui';
import { Form } from '@zolteam/zol-react-form';
// Sections
import BookingGeneralSection from '../../components/molecules/FormSections/BookingGeneralSection/BookingGeneralSection';
import BookingDepartureSection from '../../components/molecules/FormSections/BookingDepartureSection/BookingDepartureSection';
import BookingArrivalSection from '../../components/molecules/FormSections/BookingArrivalSection/BookingArrivalSection';
import BookingDemandsSection from '../../components/molecules/FormSections/BookingDemandsSection/BookingDemandsSection';
import BookingPaymentSection from '../../components/molecules/FormSections/BookingPaymentSection/BookingPaymentSection';
// Services & Domain
import { useGetAdminListQuery } from '../../services/users/usersQueries';
import { createBooking, updateBooking } from '../../services/bookings/bookingsService';
import { useGetBookingQuery } from '../../services/bookings/bookingsQueries';
import { getFullname } from '../../domain/customers/customersUseCases';
import { getAirportLabel } from '../../domain/airports/airportUseCases';
// Locales
import fr from '../../locales/fr.json';
import { useBookings } from '../../domain/bookings/useBookings';
import BookingNoteSection from '../../components/molecules/FormSections/BookingNoteSection/BookingNoteSection';

const BOOKING_FORM = 'bookingForm';

/**
 * UserForm component.
 * @returns {JSX.Element}
 */
function BookingNew() {
  const { id } = useParams();
  // states
  const [departureAirport, setDepartureAirport] = useState(null);
  const [arrivalAirport, setArrivalAirport] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [departureAirportError, setDepartureAirportError] = useState('');
  const [arrivalAirportError, setArrivalAirportError] = useState('');
  const [customerError, setCustomerError] = useState('');
  const [isPaymentHold, setIsPaymentHold] = useState(false);
  const [status, setStatus] = useState(null);
  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: admins } = useGetAdminListQuery({
    pageNumber: 1,
    pageSize: 100,
  });
  const {
    data: booking,
  } = useGetBookingQuery(
    id,
    { enabled: !!id },
  );
  const {
    BOOKING_FORM_SCHEMA,
    bookingDefaultValues,
    splitBookingForm,
    bookingFormDataToPayload,
  } = useBookings({
    users: admins?.data,
    booking,
  });

  useEffect(() => {
    setIsPaymentHold(!!booking?.paymentHoldLink);
    setCustomer(booking?.customer
      ? {
        value: booking?.customer?.id,
        label: getFullname(booking?.customer.firstname, booking?.customer.lastname),
      } : null);
    setDepartureAirport(booking?.departure.airport ? {
      value: booking?.departure.airport.airportCode,
      label: getAirportLabel(
        booking?.departure.airport.airportCode,
        booking?.departure.airport.city,
      ),
    } : null);
    setArrivalAirport(booking?.arrival.airport ? {
      value: booking?.arrival.airport.airportCode,
      label: getAirportLabel(
        booking?.arrival.airport.airportCode,
        booking?.arrival.airport.city,
      ),
    } : null);
    setStatus(booking?.status || '');
  }, [booking]);

  // API Calls
  const postCreateBookingMutation = useMutation(createBooking, {
    onSuccess: async () => {
      navigate(localStorage.getItem('bookingListUrl') || '/bookings');
    },
  });
  const putUpdateBookingMutation = useMutation(updateBooking, {
    onSuccess: async () => {
      navigate(localStorage.getItem('bookingListUrl') || '/bookings');
    },
  });

  // Handlers
  /**
   * Handles the form submission to create a new user.
   * @param {any} formValues - The values from the form.
   * @returns {void}
   */
  const onSubmit = (formValues) => {
    if (!departureAirportError && !arrivalAirportError && !customerError) {
      const body = bookingFormDataToPayload({
        formValues, departureAirport, arrivalAirport, customer, status, isPaymentHold,
      });
      if (id) {
        putUpdateBookingMutation.mutate(Object.assign(body, { id: parseInt(id, 10) }));
      } else {
        postCreateBookingMutation.mutate(body);
      }
    }
  };

  /**
   * Handle the departure airport blur.
   * @param {any} formInfo - The form info.
   */
  const handleDepartureBlur = (formInfo) => {
    if (!formInfo.formMethods.getValues().departureAirport) {
      formInfo.formMethods.setError('departureAirport', { message: t('formValidation.string.required') });
      setDepartureAirportError(t('formValidation.string.required'));
    } else {
      formInfo.formMethods.setError('passwordConfirm', null);
      setDepartureAirportError(null);
    }
  };

  /**
   * Handle the arrival airport blur.
   * @param {any} formInfo - The form info.
   */
  const handleArrivalBlur = (formInfo) => {
    if (!formInfo.formMethods.getValues().arrivalAirport) {
      formInfo.formMethods.setError('arrivalAirport', { message: t('formValidation.string.required') });
      setArrivalAirportError(t('formValidation.string.required'));
    } else {
      formInfo.formMethods.setError('arrivalAirport', null);
      setArrivalAirportError(null);
    }
  };

  /**
   * Handle the customer blur.
   * @param {any} formInfo - The form info.
   */
  const handleCustomerBlur = (formInfo) => {
    if (!formInfo.formMethods.getValues().customerId) {
      formInfo.formMethods.setError('customerId', { message: t('formValidation.string.required') });
      setCustomerError(t('formValidation.string.required'));
    } else {
      formInfo.formMethods.setError('customerId', null);
      setCustomerError(null);
    }
  };

  /**
   * Set the customer.
   * @param {{value: string, label: string}} newCustomer - The customer.
   * @param {any} formInfo
   */
  const handleSetCustomer = (newCustomer, formInfo) => {
    setCustomer(newCustomer);
    formInfo.formMethods.setValue('customerId', newCustomer?.value, { shouldValidate: true });
    handleCustomerBlur(formInfo);
  };

  /**
   * Set the status.
   * @param {string} newStatus - The status.
   * @param {any} formInfo
   */
  const handleSetStatus = (newStatus, formInfo) => {
    setStatus(newStatus);
    formInfo.formMethods.setValue('status', newStatus, { shouldValidate: true });
  };

  /**
   * Set the departure airport.
   * @param {{value: string, label: string}} airport - The airport.
   * @param {any} formInfo
   */
  const handleSetDepartureAirport = (airport, formInfo) => {
    setDepartureAirport(airport);
    formInfo.formMethods.setValue('departureAirport', airport?.value, { shouldValidate: true });
    handleDepartureBlur(formInfo);
  };

  /**
   * Set the arrival airport.
   * @param {{value: string, label: string}} airport - The airport.
   * @param {any} formInfo - The form info.
   */
  const handleSetArrivalAirport = (airport, formInfo) => {
    setArrivalAirport(airport);
    formInfo.formMethods.setValue('arrivalAirport', airport?.value, { shouldValidate: true });
    handleArrivalBlur(formInfo);
  };

  const flightBriefLink = useMemo(() => {
    if (!booking) {
      return null;
    }
    if (!booking.flightBriefUuid) {
      return `/bookings/${id}/flight-brief`;
    }
    return `/bookings/${id}/flight-brief/${booking.flightBriefUuid}`;
  }, [booking]);

  const addBriefTitle = useMemo(() => {
    if (!booking) {
      return null;
    }
    if (!booking.flightBriefUuid) {
      return t('bookingForm.actions.addBrief');
    }
    return t('bookingForm.actions.editBrief');
  }, [booking]);

  return (
    <div className="padding-l relative">
      <div
        id="form-header"
        className="d-flex f-row gap-s f-wrap justify-between padding-top-l sticky-top background-color-neutral-00 foreground-index"
      >
        <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
          {booking ? t('bookingForm.title.update') : t('bookingForm.title.new')}
        </h1>
        <div className="d-flex f-row gap-s f-wrap">
          <Button color="neutral00" className="underline">
            <a href="#general">{t('bookingForm.section.general')}</a>
          </Button>
          <Button color="neutral00" className="underline">
            <a href="#departure">{t('bookingForm.section.departure')}</a>
          </Button>
          <Button color="neutral00" className="underline">
            <a href="#arrival">{t('bookingForm.section.arrival')}</a>
          </Button>
          <Button color="neutral00" className="underline">
            <a href="#demands">{t('bookingForm.section.demands')}</a>
          </Button>
          <Button color="neutral00" className="underline">
            <a href="#payment">{t('bookingForm.section.payment')}</a>
          </Button>
          <Button color="neutral00" className="underline">
            <a href="#note">{t('bookingForm.section.note')}</a>
          </Button>
          {id ? (
            <Button color="primary">
              <Link to={flightBriefLink}>{addBriefTitle}</Link>
            </Button>
          ) : null}
        </div>
      </div>
      <div className="form-content">
        {postCreateBookingMutation.isError ? (
          <div className="margin-bottom-m">
            <Message>
              {t(`apiErrors.${postCreateBookingMutation.error
                // @ts-ignore
                .code}`)}
            </Message>
          </div>
        ) : null}
        {putUpdateBookingMutation.isError ? (
          <div className="margin-bottom-m">
            <Message>
              {t(`apiErrors.${putUpdateBookingMutation.error
                // @ts-ignore
                .code}`)}
            </Message>
          </div>
        ) : null}

        {(id && booking) || !id ? (
          <Form
            name={BOOKING_FORM}
            callbacks={{ onSubmit }}
            schema={BOOKING_FORM_SCHEMA}
            defaultValues={bookingDefaultValues}
            translations={{ formValidation: fr.formValidation }}
          >
            {(formConfig, formInfo) => {
              const formConfigParts = splitBookingForm(formConfig);

              return (
                <>
                  <div id="general">
                    <BookingGeneralSection
                      formConfig={formConfigParts.generalPart}
                      formInfo={formInfo}
                      status={status}
                      customer={customer}
                      customerError={customerError}
                      handleSetStatus={handleSetStatus}
                      handleSetCustomer={handleSetCustomer}
                      handleCustomerBlur={handleCustomerBlur}
                    />
                  </div>
                  <div id="departure">
                    <BookingDepartureSection
                      formConfig={formConfigParts.departurePart}
                      formInfo={formInfo}
                      departureAirport={departureAirport}
                      departureAirportError={departureAirportError}
                      handleSetDepartureAirport={handleSetDepartureAirport}
                      handleDepartureBlur={handleDepartureBlur}
                    />
                  </div>
                  <div id="arrival">
                    <BookingArrivalSection
                      formConfig={formConfigParts.arrivalPart}
                      formInfo={formInfo}
                      arrivalAirport={arrivalAirport}
                      arrivalAirportError={arrivalAirportError}
                      handleSetArrivalAirport={handleSetArrivalAirport}
                      handleArrivalBlur={handleArrivalBlur}
                    />
                  </div>
                  <div id="demands">
                    <BookingDemandsSection
                      formConfig={formConfigParts.demandsPart}
                      formInfo={formInfo}
                    />
                  </div>
                  <div id="payment">
                    <BookingPaymentSection
                      formConfig={{
                        payment: formConfigParts.paymentPart,
                        paymentHold: formConfigParts.paymentHoldPart,
                      }}
                      formInfo={formInfo}
                      isPaymentHold={isPaymentHold}
                      setIsPaymentHold={setIsPaymentHold}
                    />
                  </div>
                  <div id="note">
                    <BookingNoteSection
                      formConfig={formConfigParts.notePart}
                      formInfo={formInfo}
                    />
                  </div>
                  <div>
                    <Button
                      className="margin-top-m"
                      form={BOOKING_FORM}
                      isSubmit
                      isLoading={
                        postCreateBookingMutation.isLoading || putUpdateBookingMutation.isLoading
                      }
                    >
                      {id ? t('bookingForm.actions.update') : t('bookingForm.actions.submit')}
                    </Button>
                  </div>
                </>
              );
            }}
          </Form>
        ) : null}
      </div>
    </div>
  );
}

export default BookingNew;
